/**
 * Zahlungen: Zahlungsmodus, Raten, Ratenvorlagen und die Ableitung eines Ratenplans aus
 * Freitext-Zahlungsbedingungen - 1:1-Portierung aus `index.html` (Stand 2026-08-08). Die Rechenwege
 * sind woertlich uebernommen, auch wo sie umstaendlich wirken oder auf den ersten Blick fehlerhaft
 * aussehen; ergaenzt wurden ausschliesslich Typen. Namen und Logik sind unangetastet.
 *
 * Zuschnitt dieses Moduls: Durchgang 5a (Konstruktion und Normalisierung, 13 Funktionen) UND
 * Durchgang 5b (die Ableitungen: `payProgress`, `payAmount`, `isPaymentFixed`,
 * `orderOpenPaymentAmount`, `orderListPaymentState`, `orderListOverallPaymentState`,
 * `orderListTermsClass`, `ensurePaymentRequestTask`, `backfillMissingPaymentRequestTask`, plus die
 * drei Zulieferer `calculatedOrderTotal`, `orderTotal` und `applyResponsibilityToTask`), geliefert in
 * zwei Commits.
 *
 * `orderOpenPayments` (index.html:13802) steht NICHT hier: sie ruft `effRate(o)` auf, das ueber
 * `fxMix` und `orderFx` an `DB.fxDeals` haengt. `fx.ts` (Task 6) importiert seinerseits `payAmount`
 * von hier (`paymentUsdRate`) - laege `orderOpenPayments` in diesem Modul, entstuende der
 * Importzyklus payments -> fx -> payments. Sie wandert deshalb vollstaendig nach Task 6.
 *
 * `applyResponsibilityToTask` loest im Original ueber `responsibilityModuleById` ->
 * `activeResponsibilityModules()` -> `activeTaskTemplateEntity().responsibilityModules` globalen
 * Zustand auf (index.html:8834-8843). Aufgeloest zu einem zusaetzlichen Parameter
 * `responsibilityModules: ResponsibilityModule[]`, den `ensurePaymentRequestTask` und
 * `backfillMissingPaymentRequestTask` an ihre Aufrufer durchreichen (jeweils als letztes Argument,
 * Vorgabewert `[]`). `responsibilityModuleById` und `activeResponsibilityModules` gehen darin auf und
 * sind keine eigenen Symbole.
 *
 * ================= Umzug aus lib/status.ts =================
 *
 * `isPaid` (5058), `nextDue` (5143), `isPaymentRequestTask` (5177) und `openPaymentRequestTask`
 * (5223) waren in Task 3b bereits modulintern in `lib/status.ts` portiert, weil sechs der dortigen
 * Funktionen sie zwingend brauchen. Ihr fachlicher Ort ist dieses Modul; sie stehen deshalb ab jetzt
 * HIER, und `lib/status.ts` importiert sie von hier. Ein zweites Mal portiert wurden sie nicht.
 *
 * Die Richtung des Imports ist erzwungen: `lib/status.ts` -> `lib/payments.ts`, nie umgekehrt. Dieses
 * Modul kommt ohne `lib/status.ts` aus. Damit entsteht kein Importzyklus.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. Dieselbe Form haben `PAYMENT_MODES[o?.paymentMode]` (4883) und `PAYMENT_STATUSES[payment?.status]`
 *    (4886). Dort tritt der durchgegriffene Wert sogar NACH AUSSEN: `paymentModeOf({paymentMode:
 *    'constructor'})` liefert `'constructor'`, `paymentStatusOf({status: 'toString'})` liefert
 *    `'toString'` - beide gelten als "bekannter Wert", weil der Nachschlag wahrhaftig ist.
 *    REPARIERT am 2026-08-11 (Befund 8, Option B) - siehe Kommentare an `paymentModeOf` und
 *    `paymentStatusOf`.
 * 2. `normalizeOrderPayments` (4929) hat einen toten Zweig: `normalized.length ? ... : !!o.paid`. Die
 *    `while`-Schleife darueber fuellt `normalized` immer auf mindestens `defaults.length` auf, und
 *    `defaults` hat je nach Modus eine oder zwei Vorgaben - `normalized.length` ist danach nie 0.
 *    Der Zweig bleibt unveraendert stehen.
 * 3./4. `syncPaymentDocumentTypes` (index.html:4932, bisher die Eigenheiten 3 und 4) ist NICHT uebernommen: sie
 *    fuehrte die Split-Belegtypen (`zahlungsbeleg_anzahlung`/`zahlungsbeleg_restzahlung`) beim Wechsel nach
 *    "Gesamtzahlung" auf den einen `zahlungsbeleg` zusammen. Mit dem Anzahlungs- und dem Restzahlungs-/Gesamtzahlungsbeleg (Asana
 *    1220057765558651, Spec 2026-10-31) gibt es weder die Split-Typen noch den Sammeltyp; welcher Beleg zu einer
 *    Rate gehoert, entscheidet ihre Position im Plan (`lib/zahlungsbelege.ts`), nicht der Zahlungsmodus.
 * 5. `paymentsFromPiTerms` (11714) liefert Raten OHNE `status` und OHNE `usdRate`; der Unterschied
 *    bleibt erhalten, `normalizePaymentRow` ergaenzt die Felder spaeter.
 *
 * ================= Notwendige Abhaengigkeiten =================
 *
 * `uid` (index.html:4675) und `cleanPiValue` (10583) stehen nicht auf der Portierungsliste dieses
 * Tasks, werden von ihr aber gebraucht. Sie sind hier modulintern und bewusst NICHT exportiert -
 * dasselbe Vorgehen wie bei `orderExcelCell` in `format.ts` und `supplierById` in `suppliers.ts`.
 * Sobald ein Folgetask sie eigenstaendig portiert, ist diese Fassung durch einen Import zu ersetzen.
 *
 * `normSearch` (6349) wird von `inferPaymentMode` und `paymentsFromPiTerms` gebraucht und war bisher
 * modulintern in `lib/status.ts`. Es liegt jetzt als einzige Fassung in `lib/format.ts` (dem Modul,
 * das beide importieren) - siehe Begruendung dort.
 */
import {
  PAYMENT_MODES,
  PAYMENT_STATUSES,
  PAYMENT_TASK_MIGRATION_START,
  PAYMENT_TEMPLATES,
} from "../config/payments.ts";
import { addDays, normSearch, todayIso } from "./format.ts";

// ================= Typen =================
//
// Die Typdeklarationen dieses Moduls stehen seit Task 1 gebuendelt in `lib/types.ts` (Begruendung
// dort: `Supplier` war hier und in `suppliers.ts` nur in einer Richtung zuweisbar). Re-Export unter
// den bisherigen Namen, damit kein bestehender Importpfad bricht.
import type {
  Order,
  OrderPayment,
  OrderTask,
  OrderDocument,
  ResponsibilityModule,
} from "./types.ts";
export type {
  Order,
  OrderPayment,
  OrderTask,
  OrderDocument,
  ResponsibilityModule,
};

// ================= Modulinterne Abhaengigkeiten =================

/** index.html:4675 - modulintern, siehe "Notwendige Abhaengigkeiten" im Kopfkommentar. */
const uid = (): string => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

/** index.html:10583 - modulintern; einzige Verwendung hier ist `paymentsFromPiTerms`. */
function cleanPiValue(v: unknown): string {
  return String(v || "")
    .split("\n")[0]
    .replace(/^[\s:#.\-–]+|[\s;,.]+$/g, "")
    .trim();
}

// ================= Zahlungsmodus =================

/** index.html:4873 */
export function inferPaymentMode(o: Order | null | undefined): string {
  // Der Cast ist reine Typarbeit: an dieser Stelle ist `o` zur Laufzeit zwangslaeufig ein Objekt,
  // sonst waere `Array.isArray(o?.payments)` falsch.
  const payments = Array.isArray(o?.payments) ? ((o as Order).payments as unknown[]) : [];
  if (payments.length > 1) return "split";
  const terms = normSearch(o?.paymentTerms || "");
  const hasDeposit = /\b(deposit|down payment|advance|anzahlung)\b/.test(terms);
  const hasBalance = /\b(balance|restzahlung|remaining)\b/.test(terms);
  const percentages = terms.match(/\b\d{1,3}\s*%/g) || [];
  return hasDeposit && (hasBalance || percentages.length >= 2) ? "split" : "total";
}

/**
 * index.html:4882. ABWEICHUNG vom Original (Befund 8, Option B, entschieden 2026-08-11):
 * `Object.hasOwn(PAYMENT_MODES, ...)` statt eines Wahrheitstests auf dem Nachschlagergebnis. Im
 * Original tritt bei einem Prototyp-Schluessel (`paymentMode: "constructor"`) der durchgegriffene
 * Wert nach aussen UND wird von `normalizeOrderPayments` bei jedem Speichern weitergeschrieben.
 * Ein unbekannter Schluessel ist kein bekannter Modus - hier gab es keine offene Fachfrage, nur
 * eine fehlende Absicherung. Fuer EIGENE Schluessel ist die Pruefung gleichwertig: alle Werte in
 * PAYMENT_MODES sind wahrhaftige Objekte (config/payments.ts:144-147).
 */
export function paymentModeOf(o: Order | null | undefined): string {
  return Object.hasOwn(PAYMENT_MODES, o?.paymentMode as string)
    ? ((o as Order).paymentMode as string)
    : inferPaymentMode(o);
}

/**
 * index.html:4885. ABWEICHUNG vom Original (Befund 8, Option B, 2026-08-11) - dieselbe Absicherung
 * wie in `paymentModeOf`; alle Werte in PAYMENT_STATUSES sind wahrhaftige Objekte
 * (config/payments.ts:156-160).
 */
export function paymentStatusOf(payment: OrderPayment | null | undefined): string {
  if (Object.hasOwn(PAYMENT_STATUSES, payment?.status as string))
    return (payment as OrderPayment).status as string;
  return payment?.paid ? "paid" : "open";
}

// ================= Raten anlegen und normalisieren =================

/** index.html:4889 */
export function createPayment(label: unknown, pct: unknown = ""): OrderPayment {
  return {
    id: uid(),
    label,
    pct,
    amount: "",
    due: "",
    status: "open",
    usdRate: "",
    paid: false,
    paidDate: "",
  };
}

/** index.html:4895 */
export function paymentRowsForMode(mode: unknown): OrderPayment[] {
  return mode === "split"
    ? [createPayment("Anzahlung"), createPayment("Restzahlung")]
    : [createPayment("Gesamtzahlung", "100")];
}

/**
 * index.html:4900. VERAENDERT die uebergebene Rate an Ort und Stelle und liefert genau sie zurueck
 * (nur ohne Rate wird eine neue erzeugt).
 */
export function normalizePaymentRow(
  payment: OrderPayment | null | undefined,
  label: unknown,
  pct: unknown = "",
): OrderPayment {
  const row = payment || createPayment(label, pct);
  if (!row.id) row.id = uid();
  if (!row.label) row.label = label;
  if (row.pct === undefined || row.pct === null) row.pct = pct;
  if (row.amount === undefined || row.amount === null) row.amount = "";
  if (!row.due) row.due = "";
  if (row.usdRate === undefined || row.usdRate === null) row.usdRate = "";
  row.status = paymentStatusOf(row);
  row.paid = row.status === "paid";
  if (!row.paid && !row.paidDate) row.paidDate = "";
  return row;
}

/**
 * index.html:4913. VERAENDERT die uebergebene Bestellung: schreibt `paymentMode`, `payments` und
 * `paid`. Wirft bei fehlender Bestellung (`o.payments` ist ungeprueft, index.html:4915).
 */
export function normalizeOrderPayments(o: Order): OrderPayment[] {
  const mode = paymentModeOf(o);
  const source = Array.isArray(o.payments) ? (o.payments as OrderPayment[]) : [];
  const defaults: [string, string][] =
    mode === "split"
      ? [
          ["Anzahlung", ""],
          ["Restzahlung", ""],
        ]
      : [["Gesamtzahlung", "100"]];
  const normalized = source.map((payment, index) => {
    const fallback = defaults[index] || [`Weitere Zahlung ${index + 1}`, ""];
    return normalizePaymentRow(payment, fallback[0], fallback[1]);
  });
  while (normalized.length < defaults.length) {
    const fallback = defaults[normalized.length];
    normalized.push(createPayment(fallback[0], fallback[1]));
  }
  o.paymentMode = mode;
  o.payments = normalized;
  // Der `: !!o.paid`-Zweig ist tot - siehe Eigenheit 3 im Kopfkommentar. Woertlich uebernommen.
  o.paid = normalized.length ? normalized.every((payment) => payment.status === "paid") : !!o.paid;
  return normalized;
}

// ================= Gepflegte Zahlungsangaben =================

/** index.html:4951 */
export function paymentRowHasValues(payment: OrderPayment | null | undefined): boolean {
  return (
    Number(payment?.amount) > 0 ||
    Number(payment?.usdRate) > 0 ||
    !!payment?.due ||
    paymentStatusOf(payment) !== "open"
  );
}

/** index.html:4955 */
export function hasConfiguredPayments(o: Order | null | undefined): boolean {
  return ((o?.payments || []) as OrderPayment[]).some(paymentRowHasValues);
}

/**
 * index.html:11708 - Freitext-Zahlungsbedingungen zu einem Ratenplan. Acht Zweige in fester
 * Reihenfolge: Vorlagenbeschriftung im Text, `OA/open account/net N days`,
 * `N days net after shipment`, `100 % ... N days after B/L bzw. ETD`, `100 % after B/L` (mit
 * optionaler Tagesangabe), Anzahlung + `balance N days after ETD`, allgemeine Anzahlung + Rest,
 * sonst leere Liste. Die Muster sind zeichengenau uebernommen.
 *
 * ABWEICHUNG (Auftrag 2026-10-01, R5): ohne Bestelldatum bleibt die Faelligkeit leer; das Original
 * rechnete ab "heute", und das Ergebnis hing dann am Aufrufzeitpunkt.
 */
export function paymentsFromPiTerms(
  terms: unknown,
  order: Order | null | undefined,
): OrderPayment[] {
  const text = String(terms || "");
  const lower = normSearch(text);
  const template = PAYMENT_TEMPLATES.find((t) => t.id && lower.includes(normSearch(t.label)));
  const bestelldatum = (order?.orderDate || "") as string;
  // Auftrag 2026-10-01, R5: ohne Bestelldatum bleibt die Faelligkeit LEER - nie ab "heute" gerechnet.
  const faellig = (tage: unknown): string => (bestelldatum ? addDays(bestelldatum, tage as number) : "");
  if (template) {
    return template.payments.map((p) => ({
      id: uid(),
      label: p.label,
      pct: p.pct,
      amount: "",
      due: faellig(p.dueDays),
      paid: false,
      paidDate: "",
    }));
  }
  let m = text.match(/\b(?:OA|O\/A|open account|net)\s*(\d{1,3})\s*days?\b/i);
  if (m) {
    return [
      {
        id: uid(),
        label: `OA ${m[1]} days`,
        pct: "100",
        amount: "",
        due: faellig(m[1]),
        paid: false,
        paidDate: "",
      },
    ];
  }
  m =
    text.match(/\b(\d{1,3})\s*days?\s*net\s*after\s*shipment\b/i) ||
    text.match(/\bnet\s*(\d{1,3})\s*days?\s*after\s*shipment\b/i);
  if (m) {
    return [
      {
        id: uid(),
        label: `${m[1]} days net after shipment`,
        pct: "100",
        amount: "",
        due: faellig(m[1]),
        paid: false,
        paidDate: "",
      },
    ];
  }
  m =
    text.match(
      /\b100\s*%[^\n]{0,100}?\b(\d{1,3})\s*days?\s*after\s*(?:received\s*)?B\/?L(?:\s*copy)?\b/i,
    ) || text.match(/\b100\s*%[^\n]{0,100}?\b(\d{1,3})\s*days?\s*after\s*ETD\b/i);
  if (m) {
    return [
      {
        id: uid(),
        label: `${m[1]} days after BL`,
        pct: "100",
        amount: "",
        due: faellig(m[1]),
        paid: false,
        paidDate: "",
      },
    ];
  }
  m =
    text.match(/\b100\s*%\s*(?:(\d{1,3})[-\s]*days?\s*)?(?:after\s*B\/?L|after\s*BL)\b/i) ||
    text.match(
      /\b100\s*%[^\n]{0,100}(?:after\s*B\/?L|after\s*BL|after\s+received\s+B\/?L)[^\n]{0,100}?\b(\d{1,3})\s*days?\b/i,
    );
  if (m) {
    const days = m[1] || "";
    return [
      {
        id: uid(),
        label: days ? `${days} days after BL` : "After BL",
        pct: "100",
        amount: "",
        due: faellig(days),
        paid: false,
        paidDate: "",
      },
    ];
  }
  m = text.match(
    /\b(\d{1,3})\s*%[^\n]{0,60}deposit[^\n]{0,120}balance\s*(\d{1,3})\s*days?\s*after\s*ETD\b/i,
  );
  if (m) {
    const deposit = Number(m[1]) || 0;
    const balance = Math.max(0, 100 - deposit);
    return [
      {
        id: uid(),
        label: "Deposit",
        pct: String(deposit),
        amount: "",
        due: faellig(0),
        paid: false,
        paidDate: "",
      },
      {
        id: uid(),
        label: `${m[2]} days after ETD`,
        pct: String(balance),
        amount: "",
        due: faellig(m[2]),
        paid: false,
        paidDate: "",
      },
    ];
  }
  m = text.match(
    /\b(\d{1,3})\s*%[^\n]{0,40}(?:deposit|down payment|advance).*?\b(\d{1,3})\s*%.*?(?:(\d{1,3})\s*days?\s*)?(after\s*B\/?L|after\s*BL|after\s*ETD|before\s*shipment|against\s*B\/?L)?/i,
  );
  if (!m) return [];
  const days = /after\s*(?:B\/?L|ETD)/i.test(m[4] || "") ? m[3] || "" : "";
  return [
    {
      id: uid(),
      label: "Deposit",
      pct: m[1],
      amount: "",
      due: faellig(0),
      paid: false,
      paidDate: "",
    },
    {
      id: uid(),
      label: m[4] ? cleanPiValue(m[4]) : "Restzahlung",
      pct: m[2],
      amount: "",
      due: faellig(days),
      paid: false,
      paidDate: "",
    },
  ];
}

// ================= Zahlungsstand und Zahlungsanforderung =================
//
// Die vier Funktionen dieses Abschnitts waren bis Task 5a modulintern in `lib/status.ts` - siehe
// "Umzug aus lib/status.ts" im Kopfkommentar. Sie sind unveraendert uebernommen.

/** index.html:5058 - Bezahlt: mit Raten = alle Raten abgehakt, ohne Raten = einfaches Haekchen. */
export function isPaid(o: Order | null | undefined): boolean {
  return !!o?.paid;
}

/** index.html:5143 - fruehste unbezahlte Faelligkeit. */
export function nextDue(o: Order): string {
  const dues = ((o.payments || []) as OrderPayment[])
    .filter((p) => !p.paid && p.due)
    .map((p) => p.due)
    .sort();
  return (dues[0] as string) || "";
}

/** index.html:5177 */
export function isPaymentRequestTask(task: OrderTask | null | undefined): boolean {
  return task?.kind === "payment_request";
}

/** index.html:5223 */
export function openPaymentRequestTask(order: Order | null | undefined): OrderTask | null {
  return (
    ((order?.tasks || []) as OrderTask[]).find((task) => isPaymentRequestTask(task) && !task.done) ||
    null
  );
}

// ================= Ableitungen: Zahlungsbetrag, Zahlungsstand, Zahlungsanforderung =================
//
// Durchgang 5b. `orderOpenPayments` (index.html:13802) gehoert NICHT hierher - siehe Kopfkommentar.

/**
 * index.html:4688. Der Ausdruck ist eingebettet, nicht in einer Zwischenvariable gehalten (Task 7b,
 * Review-Fund aus Task 7a): eine Zwischenvariable `items` aendert bei einem wahrhaftigen Nicht-Array
 * die Meldung des geworfenen `TypeError` ("items.reduce is not a function" statt "(o.items ||
 * []).reduce is not a function") - reine Formaenderung ohne Rechenwert, siehe Differenztest.
 */
export const calculatedOrderTotal = (o: Order): number =>
  ((o.items || []) as { qty?: unknown; price?: unknown }[]).reduce(
    (s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0),
    0,
  );

/**
 * index.html:4689-4691. `Object.prototype.hasOwnProperty.call(o || {}, 'orderValueOverride')` ist
 * Bestand und bleibt zeichengleich stehen - siehe Kopfkommentar der Portierungsphase. Ohne Treffer
 * geht der Aufruf an `calculatedOrderTotal` mit dem UNVERAENDERTEN `o` (nicht `o || {}`) weiter -
 * bei `o === null`/`undefined` wirft das dort.
 */
export const orderTotal = (o: Order | null | undefined): number =>
  Object.prototype.hasOwnProperty.call(o || {}, "orderValueOverride")
    ? Math.max(0, Number((o as Order).orderValueOverride) || 0)
    : calculatedOrderTotal(o as Order);

/** index.html:5059 */
export function payProgress(o: Order): { done: number; total: number } {
  const ps = (o.payments || []) as OrderPayment[];
  return { done: ps.filter((p) => paymentStatusOf(p) === "paid").length, total: ps.length };
}

/** index.html:5064 - Ratenbetrag: fester Betrag gewinnt, sonst % vom Warenwert. */
export function payAmount(o: Order, p: OrderPayment): number {
  const fixed = Number(p.amount) || 0;
  if (fixed > 0) return fixed;
  return orderTotal(o) * (Number(p.pct) || 0) / 100;
}

/**
 * index.html:8848. VERAENDERT die uebergebene Aufgabe. Im Original loest
 * `responsibilityModuleById(responsibilityId)` das Modul global auf; hier als reine Suche im
 * uebergebenen `responsibilityModules` (siehe Kopfkommentar).
 */
export function applyResponsibilityToTask(
  task: OrderTask | null | undefined,
  responsibilityId: string,
  fallbackAssignee: string = "",
  responsibilityModules: ResponsibilityModule[] = [],
): void {
  if (!task) return;
  const module = responsibilityModules.find((module) => module.id === responsibilityId) || null;
  task.responsibilityId = module?.id || responsibilityId || "";
  task.responsibilityName = module?.name || "";
  task.assignee = module?.ownerName || fallbackAssignee;
  task.deputyAssignee = module?.deputyName || "";
}

/**
 * index.html:13021. Der Cast ist reine Typarbeit: `o?.etd` wird nur dann wahrhaftig, wenn `o`
 * selbst nicht `null`/`undefined` ist, genau die Bedingung, unter der `o.etd` rechts vom `&&`
 * ausgewertet wird.
 */
export function isPaymentFixed(o: Order | null | undefined): boolean {
  return !!(o?.etd && ((o as Order).etd as string) <= todayIso());
}

/** index.html:13025 */
export function orderOpenPaymentAmount(o: Order): number {
  const ps = (o.payments || []) as OrderPayment[];
  if (!ps.length) return isPaid(o) ? 0 : orderTotal(o);
  return ps.filter((p) => !p.paid).reduce((sum, p) => sum + payAmount(o, p), 0);
}

/** index.html:7220 - `o` wird im Original nicht gelesen, siehe Signatur dort. */
export function orderListPaymentState(
  o: Order | null | undefined,
  payment: OrderPayment | null | undefined,
): { label: string; cls: string } | null {
  if (!payment) return null;
  const status = paymentStatusOf(payment);
  if (status === "paid") return { label: "bezahlt", cls: "paid" };
  if (status === "fixed") return { label: "fix", cls: "fixed" };
  if (payment.due && (payment.due as string) < todayIso()) return { label: "offen", cls: "late" };
  return { label: "offen", cls: "open" };
}

/** index.html:7229 */
export function orderListOverallPaymentState(o: Order): { label: string; cls: string } {
  const payments = (o.payments || []) as OrderPayment[];
  if (isPaid(o)) return { label: "bezahlt", cls: "paid" };
  if (payments.some((payment) => paymentStatusOf(payment) === "paid"))
    return { label: "teilbezahlt", cls: "partial" };
  if (payments.some((payment) => paymentStatusOf(payment) === "fixed"))
    return { label: "fix", cls: "fixed" };
  return { label: "offen", cls: nextDue(o) && nextDue(o) < todayIso() ? "late" : "open" };
}

/** index.html:7237 */
export function orderListTermsClass(value: unknown): string {
  const text = String(value || "").toLowerCase();
  if (/30\s*[- ]?days?|30\s*tage?/.test(text)) return "pink";
  if (/45\s*[- ]?days?|45\s*tage?/.test(text)) return "teal";
  if (/50\s*[- ]?days?|50\s*tage?|60\s*[- ]?days?|60\s*tage?/.test(text)) return "yellow";
  if (/deposit|balance|anzahlung|restzahlung/.test(text)) return "orange";
  return "gray";
}

/**
 * index.html:5183. VERAENDERT die uebergebene Bestellung (`order.tasks`). Legt eine neue Aufgabe an
 * oder aktualisiert die vorhandene Zahlungsanforderung.
 */
export function ensurePaymentRequestTask(
  order: Order,
  requestedAt: string,
  requestedBy: unknown,
  responsibilityModules: ResponsibilityModule[] = [],
): OrderTask {
  const tasks = ((order.tasks as OrderTask[] | undefined) || []) as OrderTask[];
  order.tasks = tasks;
  let task = tasks.find((entry) => isPaymentRequestTask(entry));
  const orderLabel = order.orderNo || order.name || "Bestellung ohne Nummer";
  if (!task) {
    task = {
      id: uid(),
      kind: "payment_request",
      phaseId: "custom",
      name: "Zahlung ausführen",
      description: `Zahlung für ${orderLabel} wurde von ${requestedBy} angestoßen.`,
      due: requestedAt.slice(0, 10),
      assignee: "",
      done: false,
      createdAt: requestedAt,
      updatedAt: requestedAt,
      updatedBy: requestedBy,
    };
    applyResponsibilityToTask(task, "responsibility_payment", "Zahlung", responsibilityModules);
    tasks.push(task);
  } else {
    task.name = "Zahlung ausführen";
    task.description = `Zahlung für ${orderLabel} wurde von ${requestedBy} erneut angestoßen.`;
    task.due = requestedAt.slice(0, 10);
    applyResponsibilityToTask(task, "responsibility_payment", "Zahlung", responsibilityModules);
    task.done = false;
    task.updatedAt = requestedAt;
    task.updatedBy = requestedBy;
  }
  return task;
}

/**
 * index.html:5215. VERAENDERT die uebergebene Bestellung ueber `ensurePaymentRequestTask`.
 */
export function backfillMissingPaymentRequestTask(
  order: Order | null | undefined,
  responsibilityModules: ResponsibilityModule[] = [],
): boolean {
  const requestedAt = String(order?.paymentInitiatedAt || "");
  if (!requestedAt || requestedAt < PAYMENT_TASK_MIGRATION_START) return false;
  if ((((order as Order).tasks as OrderTask[] | undefined) || []).some((task) => isPaymentRequestTask(task))) {
    return false;
  }
  ensurePaymentRequestTask(
    order as Order,
    requestedAt,
    (order as Order).paymentInitiatedBy || "Unbekannt",
    responsibilityModules,
  );
  return true;
}
